import React, {useState} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {Card} from '@rneui/themed';
import _ from 'lodash';
import {observer} from 'mobx-react-lite';

import {AppRoute} from '../../../../models/AppRoute';
import {useStores} from '../../../../stores/use-stores';
import theme from '../../../../style/theme';
import NoTillPaymentServiceAvailableModal from '../../../modals/NoPaymentConfiguredModal';
import Button from '../../../themed/Button';
import Text from '../../../themed/Text';
import CardTokeniserTillModal from '../cardTokeniser/CardTokeniserTillModal';

import CardIcons from './CardIcons';

type PaymentMethodProps = {
  navigationTarget:
    | AppRoute.ACCOUNT_PAYMENT_METHOD
    | AppRoute.CART_ORDER_CHECKOUT_PAYMENT
    | AppRoute.CART_ORDER_PROCESSING
    | AppRoute.SHOP
    | AppRoute.GIFT_CARDS_STORE_CHECKOUT;
  showTitle?: boolean;
};

const PaymentMethod = observer(
  ({navigationTarget, showTitle = true}: PaymentMethodProps) => {
    const {paymentStore, cartStore} = useStores();

    const [isCardTokeniserTillModalVisible, setCardTokeniserTillModalVisible] =
      useState(false);
    const [
      isNoTillPaymentServiceAvailableModalVisible,
      setNoTillPaymentServiceAvailableModalVisible,
    ] = useState(false);

    const handleChangeCardPress = () =>
      !paymentStore.tillPaymentService
        ? setNoTillPaymentServiceAvailableModalVisible(true)
        : setCardTokeniserTillModalVisible(true);
    const handleDeleteCard = () =>
      paymentStore.deleteTillUserPaymentInstrument();

    const handleCardTokeniserTillModalCancel = () =>
      setCardTokeniserTillModalVisible(false);
    const handleNoTillPaymentServiceAvailableModalOK = () =>
      setNoTillPaymentServiceAvailableModalVisible(false);

    const renderContent = () => {
      const {
        tillUserPaymentInstrument: userTillPaymentInstrument,
        isLoadingUserPaymentInstruments: loadingUserPaymentInstruments,
      } = paymentStore;

      if (loadingUserPaymentInstruments) {
        return (
          <View style={styles.paymentMethodContainer}>
            <ActivityIndicator
              size="small"
              color={theme.colour.activityIndicator.primary}
            />
          </View>
        );
      }

      return userTillPaymentInstrument ? (
        <>
          <View style={{gap: 16}}>
            <View
              style={{flexDirection: 'row', justifyContent: 'space-between'}}>
              <Text color="black" variant="heading3">
                Card details
              </Text>
              <CardIcons />
            </View>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 8,
              }}>
              <View style={{flexDirection: 'column', gap: 8, flexGrow: 1}}>
                <Text style={styles.detailHeader}>Card type</Text>
                <Text style={styles.detailText}>
                  {_.upperFirst(userTillPaymentInstrument.card_type)}
                </Text>
              </View>
              <View style={{flexDirection: 'column', gap: 8}}>
                <Text style={styles.detailHeader}>Card number</Text>
                <Text style={styles.detailText}>
                  ending in{' '}
                  {userTillPaymentInstrument.obfuscated_card?.slice(-4)}
                </Text>
              </View>
              <View style={{flexDirection: 'column', gap: 8}}>
                <Text style={styles.detailHeader}>Expiry</Text>
                <Text style={styles.detailText}>
                  {userTillPaymentInstrument.expiry_month}/
                  {userTillPaymentInstrument.expiry_year.toString()?.slice(-2)}
                </Text>
              </View>
            </View>
          </View>
          <View style={styles.actions}>
            <Button
              variant="outlined"
              title="CHANGE CARD"
              onPress={handleChangeCardPress}
              // buttonStyle={styles.button}
              disabled={cartStore.submittingOrder}
            />
            <Button
              variant="outlined"
              title="DELETE CARD"
              onPress={handleDeleteCard}
              // buttonStyle={styles.button}
              disabled={cartStore.submittingOrder}
            />
          </View>
        </>
      ) : (
        <View>
          {!!showTitle && (
            <View
              style={{flexDirection: 'row', justifyContent: 'space-between'}}>
              <Text color="black" variant="heading3">
                Card details
              </Text>
              <CardIcons />
            </View>
          )}

          <Button
            variant="outlined"
            title="ADD A PAYMENT METHOD"
            onPress={handleChangeCardPress}
            containerStyle={styles.addCardButton}
            disabled={cartStore.submittingOrder}
          />
        </View>
      );
    };

    return (
      <Card noMargin variant="button" wrapperStyle={styles.wrapperStyle}>
        {renderContent()}
        <CardTokeniserTillModal
          isVisible={isCardTokeniserTillModalVisible}
          onCancel={handleCardTokeniserTillModalCancel}
          navigationTarget={navigationTarget}
        />
        <NoTillPaymentServiceAvailableModal
          onOK={handleNoTillPaymentServiceAvailableModalOK}
          isVisible={isNoTillPaymentServiceAvailableModalVisible}
        />
      </Card>
    );
  },
);

export default PaymentMethod;

const styles = StyleSheet.create({
  wrapperStyle: {
    gap: 16,
  },
  detailHeader: {
    fontWeight: '600',
    fontSize: 14,
  },
  detailText: {
    fontSize: 14,
  },
  paymentMethodContainer: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  title: {
    marginVertical: 8,
  },
  paymentNormal: {
    marginTop: 4,
    color: theme.colour.text.heading.primary,
    fontWeight: '400',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  addCardButton: {
    marginTop: 16,
  },
  button: {
    margin: 8,
    width: 160,
  },
  actions: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
});
